import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Book, ArrowLeft, Heart, Eye, Clock, DownloadSimple, TextT,
  ShareNetwork, User, CaretDown, Feather
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import AddToCollectionButton from '../components/AddToCollectionButton';
import { proxyImage } from '../lib/imageProxy';
import WorkReviews from '../components/WorkReviews';
import GlobalHeader from '../components/GlobalHeader';
import { createSafeRichHTML } from '../lib/sanitize';

/**
 * Full-screen dedicated reader for a single classical work.
 * Route: /work/:workId
 */
export default function WorkReader() {
  const { workId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [fontSize, setFontSize] = useState(20);
  const [downloadOpen, setDownloadOpen] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workId]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/works/${workId}`);
      setData(res.data);
      setLikes(res.data.work.likes || 0);
      // Register a view (throttled server-side, once per hour per session)
      axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/works/${workId}/view`,
        {}, { withCredentials: true }
      ).catch(() => {});
    } catch (e) {
      toast.error('Work not found');
      navigate('/authors');
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async () => {
    if (!user) return toast.error('Please sign in to like');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/works/${workId}/like`,
        {}, { withCredentials: true }
      );
      setLiked(res.data.liked);
      setLikes((n) => n + (res.data.liked ? 1 : -1));
    } catch (e) {
      toast.error('Failed');
    }
  };

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    } catch { toast.error('Failed to copy'); }
  };

  const downloadUrl = (ext) => `${process.env.REACT_APP_BACKEND_URL}/api/works/${workId}/download.${ext}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!data) return null;

  const { work, author } = data;

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <GlobalHeader />

      {/* Hero / metadata */}
      <div className="container mx-auto px-6 pt-12 pb-8">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-primary mb-4 block">
            {work.work_type || 'work'} {work.year ? ` · ${work.year}` : ''}
          </span>
          <h1 className="text-4xl md:text-6xl mb-3 tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }} data-testid="work-title">
            {work.title}
          </h1>
          {work.title_hindi && work.title_hindi !== work.title && (
            <h2 className="text-2xl md:text-3xl text-muted-foreground mb-4" style={{ fontFamily: 'Mukta, sans-serif' }} data-testid="work-title-hindi">
              {work.title_hindi}
            </h2>
          )}
          {author && (
            <Link to={`/author/${author.slug}`} className="inline-flex items-center gap-2 text-sm hover:text-primary transition-colors" data-testid="author-link">
              {author.photograph ? (
                <img src={proxyImage(author.photograph)} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <User size={16} className="text-muted-foreground" />
              )}
              <span>{author.name}</span>
              {author.literary_movement && <span className="text-muted-foreground">· {author.literary_movement}</span>}
            </Link>
          )}
          {work.description && (
            <p className="mt-6 text-base md:text-lg text-muted-foreground italic max-w-2xl mx-auto">{work.description}</p>
          )}

          {/* Stats bar */}
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-muted-foreground" data-testid="work-stats">
            <span className="inline-flex items-center gap-1"><Eye size={14} /> {work.views || 0} reads</span>
            <span className="inline-flex items-center gap-1"><Heart size={14} /> {likes} likes</span>
            {work.reading_time > 0 && <span className="inline-flex items-center gap-1"><Clock size={14} /> {work.reading_time} min</span>}
          </div>
        </div>
      </div>

      {/* Ornamental divider */}
      <div className="flex items-center justify-center gap-4 text-primary/40 my-4">
        <span className="w-16 h-px bg-current"></span>
        <Feather size={16} weight="duotone" />
        <span className="w-16 h-px bg-current"></span>
      </div>

      {/* Reader body */}
      <div className="container mx-auto px-6 pb-24">
        <article
          className="max-w-2xl mx-auto prose prose-lg dark:prose-invert prose-p:leading-relaxed prose-p:my-4"
          style={{
            fontFamily: 'Mukta, IBM Plex Sans, sans-serif',
            fontSize: `${fontSize}px`,
            lineHeight: 1.9,
            whiteSpace: 'pre-line'
          }}
          data-testid="work-content"
        >
          {/* If content contains HTML tags render as HTML, else render as plain text */}
          {work.content?.includes('<') ? (
            <div dangerouslySetInnerHTML={createSafeRichHTML(work.content)} />
          ) : (
            <div>{work.content}</div>
          )}
        </article>

        {/* Tags */}
        {work.tags && work.tags.length > 0 && (
          <div className="max-w-2xl mx-auto mt-12 flex flex-wrap gap-2 justify-center">
            {work.tags.map((t, i) => (
              <span key={i} className="text-xs uppercase tracking-widest bg-muted px-3 py-1 rounded-full text-muted-foreground">#{t}</span>
            ))}
          </div>
        )}

        {/* Reviews section */}
        <div className="max-w-2xl mx-auto mt-16 border-t border-border pt-8" data-testid="reviews-block">
          <h3 className="text-2xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
            Reader Reviews
          </h3>
          <WorkReviews workId={workId} />
        </div>

        {/* Return to author */}
        {author && (
          <div className="max-w-2xl mx-auto mt-12 text-center">
            <Link
              to={`/author/${author.slug}`}
              data-testid="explore-more-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
            >
              Explore more works by {author.name} →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
